/**
 * Cadastro embutido da Meta (Embedded Signup v4): o advogado conecta o número de
 * WhatsApp dele numa janela da própria Meta, sem sair do JurisApp.
 *
 * Fluxo: carrega o SDK da Meta uma vez, abre o cadastro com a configuração do
 * JurisApp (``config_id``) e espera duas coisas — o código de troca (no retorno do
 * login; vale 30 s) e os ids da conta de WhatsApp e do número (num ``message`` da
 * janela da Meta). Quem troca o código pelo token é o servidor.
 *
 * docs/PLANO_SECRETARIO_WHATSAPP_META.md, Bloco C.
 */

export interface MetaSignupParams {
  appId: string;
  configId: string;
  version: string;
}

export interface MetaSignupResult {
  code: string;
  wabaId: string;
  /** Com o app WhatsApp Business mantido, a Meta manda só a conta; o servidor busca o número. */
  phoneNumberId: string | null;
}

export class MetaSignupCancelled extends Error {
  constructor() {
    super('O cadastro na Meta foi fechado antes de terminar.');
  }
}

interface FacebookSdk {
  init(params: Record<string, unknown>): void;
  login(
    callback: (response: { authResponse?: { code?: string } | null }) => void,
    params: Record<string, unknown>,
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_URL = 'https://connect.facebook.net/pt_BR/sdk.js';
/** O evento com a conta pode chegar um pouco depois do código; espera-se por ele. */
const ACCOUNT_WAIT_MS = 5_000;

let loading: Promise<FacebookSdk> | null = null;

/**
 * Carrega o SDK antes do clique. O navegador só deixa abrir a janela da Meta no
 * mesmo gesto do usuário: com o SDK baixando no clique (celular, rede lenta,
 * Safari), a janela era bloqueada e o botão não fazia nada.
 */
export function preloadMetaSdk(params: MetaSignupParams): Promise<void> {
  return loadSdk(params).then(() => undefined);
}

function loadSdk(params: MetaSignupParams): Promise<FacebookSdk> {
  if (window.FB) return Promise.resolve(window.FB);
  if (loading) return loading;
  loading = new Promise<FacebookSdk>((resolve, reject) => {
    window.fbAsyncInit = () => {
      if (!window.FB) {
        reject(new Error('O SDK da Meta não carregou.'));
        return;
      }
      window.FB.init({ appId: params.appId, autoLogAppEvents: true, xfbml: false, version: params.version });
      resolve(window.FB);
    };
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => {
      loading = null;
      reject(new Error('Não foi possível carregar o cadastro da Meta. Verifique a conexão.'));
    };
    document.body.appendChild(script);
  });
  return loading;
}

interface SignupEvent {
  type?: string;
  event?: string;
  data?: { waba_id?: string; phone_number_id?: string; error_message?: string };
}

function readEvent(event: MessageEvent): SignupEvent | null {
  let origin: URL;
  try {
    origin = new URL(event.origin);
  } catch {
    return null;
  }
  const fromMeta = origin.hostname === 'facebook.com' || origin.hostname.endsWith('.facebook.com');
  if (origin.protocol !== 'https:' || !fromMeta) return null;
  try {
    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    return data && data.type === 'WA_EMBEDDED_SIGNUP' ? (data as SignupEvent) : null;
  } catch {
    return null;
  }
}

/**
 * Abre o cadastro da Meta e devolve o código e a conta. ``keepBusinessApp``: o
 * número continua no app WhatsApp Business do advogado (coexistência).
 *
 * Chamar direto no clique, com o SDK já carregado (``preloadMetaSdk``): a janela
 * abre de forma síncrona, dentro do gesto do usuário.
 */
export function openMetaSignup({
  configId,
  keepBusinessApp,
}: {
  configId: string;
  keepBusinessApp: boolean;
}): Promise<MetaSignupResult> {
  const fb = window.FB;
  if (!fb) return Promise.reject(new Error('O cadastro da Meta ainda está carregando. Tente em instantes.'));

  return new Promise<MetaSignupResult>((resolve, reject) => {
    let account: { wabaId: string; phoneNumberId: string | null } | null = null;
    let code: string | null = null;
    let cancelled = false;
    let metaError: string | null = null;
    let timer: number | undefined;

    const cleanup = () => {
      window.removeEventListener('message', listen);
      if (timer !== undefined) window.clearTimeout(timer);
    };
    const tryFinish = () => {
      if (code && account) {
        cleanup();
        resolve({ code, wabaId: account.wabaId, phoneNumberId: account.phoneNumberId });
      }
    };
    function listen(event: MessageEvent) {
      const data = readEvent(event);
      if (!data) return;
      if (data.event === 'CANCEL') {
        // A Meta usa o mesmo evento para quem fechou e para erro dentro do cadastro.
        cancelled = true;
        metaError = data.data?.error_message ?? null;
        return;
      }
      if (data.event?.startsWith('FINISH') && data.data?.waba_id) {
        account = { wabaId: data.data.waba_id, phoneNumberId: data.data.phone_number_id ?? null };
        tryFinish();
      }
    }
    window.addEventListener('message', listen);

    const extras: Record<string, unknown> = { setup: {}, sessionInfoVersion: '3' };
    if (keepBusinessApp) extras.featureType = 'whatsapp_business_app_onboarding';

    fb.login(
      (response) => {
        code = response.authResponse?.code ?? null;
        if (!code || cancelled) {
          cleanup();
          reject(metaError ? new Error(`A Meta não concluiu o cadastro: ${metaError}`) : new MetaSignupCancelled());
          return;
        }
        timer = window.setTimeout(() => {
          cleanup();
          reject(new Error('A Meta não informou a conta de WhatsApp escolhida. Tente de novo.'));
        }, ACCOUNT_WAIT_MS);
        tryFinish();
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras,
      },
    );
  });
}
